import React, { useEffect, useState } from 'react';
import { Container, Divider, Box, useDisclosure } from '@chakra-ui/react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormInputs } from '../types/formInputs';
import { defaultValues } from '../data/formDefaultValues';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { TravelVoucherFormValidation } from '../validation/validation';
import { BasicInformationSection } from './BasicInformationSection';
import { TravelInformationSection } from './TravelInformationSection';
import { DailyAllowanceSection } from './DailyAllowanceSection';
import { AccountingInformationSection } from './AccountingInformationSection';
import { FormActionBar } from './FormActionBar';
import {
  useRetrieveTravelRequestById,
  useTravelFormMutation,
  useUpdateTravelRequest,
} from 'api/services/TravelForm';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProjectSearchResults, TravelFormQuery } from 'api/types/types';
import { FormHeader } from './FormHeader';
import { CloseFormModal } from '../modal/CloseFormModal';
import { useAppSelector } from 'store';
import { userSelector } from 'api/slices/userSlice';
import { LoadingContainer } from 'components/LoadingContainer/LoadingContainer';
import { useGetCalculateDimensions } from 'api/services/Dimensions';
import { approversSelector } from 'api/slices/approversSlice';

const DEFAULT_DIMENSION = { name: 'Ferðakostnaður erlendis', id: 'I103022' };

export const TravelVoucherform = ({ editMode, onCancel }: any): JSX.Element => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  // const id = queryParams.get('id');
  // const role = queryParams.get('role');
  const {
    id = null,
    role = null,
    action = '',
  } = Object.fromEntries(queryParams);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const [skipValidation, setSkipValidation] = useState(false);
  const [emplId, setEmplId] = useState('');

  const previewMode = !!id;
  const isEditMode = !!editMode;
  const { data: travelRequestData, isLoading: isLoadingRequestById } =
    useRetrieveTravelRequestById(id, role);

  const { user } = useAppSelector(userSelector);
  const { approvers } = useAppSelector(approversSelector);
  const [listOfApprovers, setListOfApprovers] = useState<
    { label: string; value: string; id: string }[] | undefined
  >([]);

  const { refetch: getCalculatedDimensions, data: dimensionData } =
    useGetCalculateDimensions(user?.emplId, DEFAULT_DIMENSION?.id);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    register,
    trigger,
    formState: { errors },
  } = useForm<FormInputs>({
    // @ts-ignore
    resolver: skipValidation
      ? undefined
      : yupResolver(TravelVoucherFormValidation),
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      ...defaultValues,
    },
  });
  const setDefaultProjectValues = (data: ProjectSearchResults): void => {
    const selectedProject = data.Data;

    setValue('workProject', DEFAULT_DIMENSION.name);
    setValue('workProjectId', DEFAULT_DIMENSION.id);

    selectedProject.map((dimension) => {
      if (dimension.type === 'Location') {
        setValue('workLocation', dimension.Name);
        setValue('workLocationId', dimension.id);
      }
      if (dimension.type === 'Department') {
        setValue('workDepartment', dimension.Name || '');
        setValue('workDepartmentId', dimension.id || '');
      }
    });
  };

  const mapApproverToUser = async (
    dimension: string,
    adUser: string
  ): Promise<void> => {
    const matchingApprovers = approvers.filter((approver) =>
      approver.approvesDepartments.some(
        (department) => department.id === dimension
      )
    );
    console.log('matchingApprovers', matchingApprovers);
    if (matchingApprovers.length === 0) {
      setListOfApprovers(undefined);
      return;
    }
    // If only one approver is found, set him as default and lock the selection
    // if (matchingApprovers.length <= 1) {
    //   setValue('approverName', matchingApprovers[0].name);
    //   setValue('approverId', adUser);
    //   setListOfApprovers([]);
    // } else {
    console.log('empl', adUser.toUpperCase());
    // Create a list without the employee with the same name

    const filteredApprovers = matchingApprovers.filter(
      (approver) => approver?.employeeId?.toUpperCase() !== adUser.toUpperCase()
    );

    console.log('filteredApprovers', filteredApprovers);
    // If more than one approver is found, show a selection of approvers in a list
    const newApproverList = filteredApprovers.map((approver) => ({
      label: approver.name,
      value: approver.name,
      id: approver.employeeId,
    }));

    // If the employee is found, add them as second person in the list
    const employeeIsAlsoApprover = matchingApprovers.find(
      (approver) => approver?.employeeId?.toUpperCase() === adUser.toUpperCase()
    );

    if (employeeIsAlsoApprover) {
      newApproverList.splice(1, 0, {
        label: employeeIsAlsoApprover.name,
        value: employeeIsAlsoApprover.name,
        id: employeeIsAlsoApprover.employeeId,
      });
    }

    if (!editMode) {
      setValue('approverName', newApproverList[0].label);
      setValue('approverId', newApproverList[0].id);
    } else if (travelRequestData) {
      setValue('approverName', travelRequestData?.approverName);
      setValue('approverId', travelRequestData?.approverId);
    }

    setListOfApprovers(newApproverList);
    // }
  };

  useEffect(() => {
    if (dimensionData) {
      setDefaultProjectValues(dimensionData);
    }
    // eslint-disable-next-line
  }, [dimensionData]);

  useEffect(() => {
    if (user && !id && approvers.length > 0) {
      getCalculatedDimensions();
      const { name, adUser, dimensions, emplId: userEmplId, ssn } = user;
      setValue('employeeId', adUser || '');
      setValue('employeeName', name);
      setValue('ssn', ssn);
      setEmplId(userEmplId);
      mapApproverToUser(dimensions[0], adUser);
    }
    // eslint-disable-next-line
  }, [user, id, approvers]);

  useEffect(() => {
    if (id) {
      if (travelRequestData?.message) {
        navigate(-1);
      } else if (travelRequestData) {
        Object.keys(travelRequestData).forEach((fieldName) => {
          const key = fieldName as keyof TravelFormQuery;
          const value = travelRequestData[key];
          if (
            fieldName !== 'status' &&
            fieldName !== 'erpId' &&
            fieldName !== 'id'
          ) {
            if (fieldName === 'arrangeAccommodation') {
              setValue(fieldName, travelRequestData[fieldName].toString());
            } else if (fieldName === 'arrangeFlight') {
              setValue(fieldName, travelRequestData[fieldName].toString());
            } else if (fieldName === 'addExtraDays') {
              if (typeof value === 'number' && value > 0) {
                setValue('radioExtraDays', 'true');
                setValue(fieldName, value);
              }
            } else {
              console.log(fieldName, travelRequestData[fieldName]);
              setValue(fieldName as any, travelRequestData[fieldName]);
            }
          }
        });
        // setValue('approverName', newApproverList[0].label);
        // setValue('approverId', newApproverList[0].id);
        mapApproverToUser(
          travelRequestData.workDepartmentId,
          travelRequestData.employeeId
        );
      }
    }
    // eslint-disable-next-line
  }, [travelRequestData, id]);

  const {
    mutate: createTravelRequest,
    isLoading: isCreatingTravelRequest,
    isSuccess: travelRequestCreated,
  } = useTravelFormMutation();

  const {
    mutateAsync: updateTravelRequest,
    isLoading: isUpdatingTravelRequest,
    isSuccess: travelRequestUpdated,
  } = useUpdateTravelRequest(id);

  useEffect(() => {
    if (travelRequestCreated) {
      reset();
      navigate('/ferdabeidnir');
    }
    if (travelRequestUpdated) navigate('/ferdabeidnir');
    // eslint-disable-next-line
  }, [travelRequestCreated, travelRequestUpdated]);

  useEffect(() => {
    if (id && !action && travelRequestData) {
      // reset();
      if (travelRequestData?.status?.shortName !== 'DRAFT')
        navigate(`/ferdabeidnir/ferdabeidni/preview?id=${id}&action=beidni`);
    }
    // eslint-disable-next-line
  }, [id, travelRequestData]);

  const onSubmit: SubmitHandler<FormInputs> = async (
    formData: FormInputs,
    event: React.BaseSyntheticEvent<object, any, any> | undefined
  ) => {
    event?.preventDefault();
    const submitButtonType = event?.target.name;
    const isDraft = submitButtonType === 'save-draft';

    const isValid = await trigger();

    const { customCountry, radioExtraDays, ...submitData } = formData;

    const formattedSubmitData = {
      ...submitData,
      country: formData.customCountry || formData.country,
      arrangeAccommodation: formData.arrangeAccommodation === 'true',
      arrangeFlight: formData.arrangeFlight === 'true',
      departureDate: formData.departureDate
        ? moment(formData.departureDate).format('yyyy-MM-DD')
        : moment().format('yyyy-MM-DD'),
      arrivalDate: formData.arrivalDate
        ? moment(formData.arrivalDate).format('yyyy-MM-DD')
        : moment().format('yyyy-MM-DD'),
      addExtraDays: Number(formData.addExtraDays) || 0,
    };
    const statusname = travelRequestData?.status.shortName || 'PENDING';
    if (isValid) {
      const submitQuery: TravelFormQuery = {
        status: {
          shortName: isDraft ? 'DRAFT' : statusname,
        },
        ...formattedSubmitData,
      };
      if (previewMode) {
        try {
          await updateTravelRequest(submitQuery);
          // await updateTravelRequestStatus({ shortName: 'PENDING' });
        } catch (e) {
          console.error(e);
        }
      } else {
        createTravelRequest(submitQuery);
      }
    }
  };

  return (
    <>
      {!editMode && <FormHeader previewMode={previewMode} id={id} />}
      <Container maxW="container.xl" mb={10} mt={10} marginStart="ltr">
        <Box as="form" onSubmit={handleSubmit(onSubmit)}>
          <LoadingContainer isLoaded={id ? !isLoadingRequestById : true}>
            {/* SKREF 1 - Grunnupplýsingar */}
            {/* Disaable if editing - Grunnupplýsingar */}
            {/* {!editMode && (
              <> */}
            <BasicInformationSection
              errors={errors}
              register={register}
              setValue={setValue}
              watch={watch}
              control={control}
              editMode={false}
              listOfApprovers={listOfApprovers}
              mapApproverToUser={mapApproverToUser}
              setEmplId={setEmplId}
            />
            <Divider marginTop={10} marginBottom={10} />
            {/* </>
            )} */}

            {/* SKREF 2 - Ferðaupplýsingar */}
            <TravelInformationSection
              errors={errors}
              register={register}
              control={control}
              setValue={setValue}
              watch={watch}
            />
            <Divider marginTop={10} marginBottom={10} />
            {/* SKREF 3 - Dagpeningar */}
            <DailyAllowanceSection watch={watch} setValue={setValue} />
            <Divider marginTop={10} marginBottom={10} />
            {/* SKREF 4 - Bókhaldsupplýsingar */}
            <AccountingInformationSection
              errors={errors}
              register={register}
              setValue={setValue}
              watch={watch}
              control={control}
              emplId={emplId}
            />
          </LoadingContainer>
          <FormActionBar
            isSubmitting={isCreatingTravelRequest || isUpdatingTravelRequest}
            previewMode={previewMode}
            status={travelRequestData?.status?.shortName}
            editMode={isEditMode}
            id={id}
            onOpen={onOpen}
            onCancel={onCancel}
            setSkipValidation={setSkipValidation}
          />
          <CloseFormModal
            isOpen={isOpen}
            onClose={onClose}
            handleSubmit={handleSubmit(onSubmit)}
            isSubmitting={isCreatingTravelRequest}
            setSkipValidation={setSkipValidation}
          />
        </Box>
      </Container>
    </>
  );
};
